// src/modules/employees/EmployeeList.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaEdit, FaTrash, FaEnvelope, FaPhoneAlt, FaHardHat, FaInbox,
  FaSortAlphaDown, FaSortAlphaUp, FaSort, FaClock, FaDollarSign,
} from 'react-icons/fa';
import { listEmployees, deleteEmployee } from './employeeService';
import { formatCurrency } from '../../utils/utils';
import Pagination from '../../components/shared/Pagination';
import Modal from '../../components/shared/Modal';
import DashboardLayout from '../../layouts/DashboardLayout';
import formatPhone from '../../components/shared/formatPhone';
import { StatusBadge, ResponsiveHeader } from '../../components/styled/CommonStyles';
import {
  GridContainer, Card, CardHeader, LeadName, CardContent, InfoRow,
  ActionIconsContainer, ActionButton, EmptyState, FilterGroup, FilterLabel,
  FilterButton, FilterSection,
} from '../../components/styled/GridComponents';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortConfig, setSortConfig] = useState({ field: 'username', direction: 'asc' });
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  const navigate = useNavigate();

  useEffect(() => { fetchEmployees(); }, []);

  const fetchEmployees = async () => {
    setLoading(true); setError(null);
    try {
      setEmployees(await listEmployees());
    } catch (e) {
      setError('Failed to load employees. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteEmployee(toDelete.id);
      setEmployees((list) => list.filter((u) => u.id !== toDelete.id));
      setFeedbackMessage('Employee deleted successfully');
      setFeedbackSuccess(true);
    } catch (e) {
      setFeedbackMessage('Failed to delete employee: ' + (e.response?.data?.message || e.message));
      setFeedbackSuccess(false);
    } finally {
      setShowFeedback(true);
      setLoading(false);
      setDeleteModalOpen(false);
      setToDelete(null);
    }
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const list = employees.filter((u) =>
      !q || u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
    return list.sort((a, b) => {
      const av = (a[sortConfig.field] || '').toString();
      const bv = (b[sortConfig.field] || '').toString();
      return sortConfig.direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [employees, searchQuery, sortConfig]);

  const current = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleSort = (field) => setSortConfig((p) => ({
    field, direction: p.field === field && p.direction === 'asc' ? 'desc' : 'asc',
  }));

  return (
    <DashboardLayout>
      <ResponsiveHeader title="Employees" onSearch={setSearchQuery} onAdd={() => navigate('/employees/new')} />

      {loading && <LoadingSpinner />}
      {error && !loading && <EmptyState><p>{error}</p></EmptyState>}

      {!loading && !error && (
        <>
          <FilterSection>
            <FilterGroup>
              <FilterLabel>Sort by:</FilterLabel>
              <FilterButton isActive={sortConfig.field === 'username'} onClick={() => handleSort('username')}>
                {sortConfig.field === 'username' ? (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />) : <FaSort />} Name
              </FilterButton>
              <FilterButton isActive={sortConfig.field === 'email'} onClick={() => handleSort('email')}>
                {sortConfig.field === 'email' ? (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />) : <FaSort />} Email
              </FilterButton>
            </FilterGroup>
          </FilterSection>

          <GridContainer>
            {current.length === 0 ? (
              <EmptyState>
                <FaInbox size={48} />
                <p>No employees yet. Add a field employee to enable mobile clock-in.</p>
              </EmptyState>
            ) : current.map((u) => (
              <Card key={u.id}>
                <CardHeader>
                  <LeadName>{u.username}</LeadName>
                  <StatusBadge color="#F69620"><FaHardHat style={{ marginRight: 5 }} />Employee</StatusBadge>
                </CardHeader>
                <CardContent>
                  <InfoRow><FaEnvelope /><span>{u.email}</span></InfoRow>
                  {u.phoneNumber && <InfoRow><FaPhoneAlt /><span>{formatPhone(u.phoneNumber)}</span></InfoRow>}
                  <InfoRow>
                    <FaDollarSign />
                    <span>{u.wage_rate != null && u.wage_rate !== ''
                      ? `${formatCurrency(parseFloat(u.wage_rate))}/hr`
                      : 'No pay rate set'}</span>
                  </InfoRow>
                </CardContent>
                <ActionIconsContainer>
                  <ActionButton onClick={() => navigate(`/time-tracking?userId=${u.id}`)} title="View shifts">
                    <FaClock />
                  </ActionButton>
                  <ActionButton onClick={() => navigate(`/employees/edit/${u.id}`)} title="Edit employee">
                    <FaEdit />
                  </ActionButton>
                  <ActionButton onClick={() => { setToDelete(u); setDeleteModalOpen(true); }} title="Delete employee">
                    <FaTrash />
                  </ActionButton>
                </ActionIconsContainer>
              </Card>
            ))}
          </GridContainer>
        </>
      )}

      {!loading && filtered.length > itemsPerPage && (
        <Pagination itemsPerPage={itemsPerPage} totalItems={filtered.length}
          paginate={setCurrentPage} currentPage={currentPage} />
      )}

      {deleteModalOpen && (
        <Modal title="Delete Employee"
          message={`Are you sure you want to delete ${toDelete?.username || ''}? This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => { setDeleteModalOpen(false); setToDelete(null); }}
          isOpen={deleteModalOpen} />
      )}

      <FeedbackModal isOpen={showFeedback} message={feedbackMessage}
        success={feedbackSuccess} onComplete={() => setShowFeedback(false)} />
    </DashboardLayout>
  );
};

export default EmployeeList;
